import { createContext, useContext, useSyncExternalStore, type ReactElement } from "react";
import { ServerNotice, TextLink } from "../ui/kit/index.ts";
import { say } from "../words.ts";
import { INITIAL_SERVER_VIEW, serverNotice, type ServerView } from "./server-status.ts";
import type { StatusPoll } from "./status-poll.ts";

/**
 * The server's state on the entry screens (ADR-078 decision 7): the poll the page runs while an entry screen is up,
 * shared through a context so every entry screen draws the same strip, and the strip itself, over the window, as wide
 * as the window. With no poll (a `?token=` page, the gallery, a test's static render) nothing is drawn.
 */

/**
 * What the entry screens share: the poll, and what Details does, which opens the maintenance page, or null where no
 * page can be opened.
 */
export interface ServerStatusSource {
  readonly poll: StatusPoll;
  readonly onDetails: (() => void) | null;
}

/**
 * The entry screens' server status, null when the page runs none.
 */
export const ServerStatusContext = createContext<ServerStatusSource | null>(null);

/**
 * Subscribes to nothing, for a render with no poll.
 *
 * @returns A no-op unsubscribe.
 */
function subscribeNothing(): () => void {
  return () => undefined;
}

/**
 * The view before any poll.
 *
 * @returns The initial view.
 */
function noView(): ServerView {
  return INITIAL_SERVER_VIEW;
}

/**
 * The poll's view, redrawn on each change, or the initial view with no poll; a static render reads the poll's view
 * too.
 *
 * @returns The view.
 */
export function useServerView(): ServerView {
  const source = useContext(ServerStatusContext);
  const snapshot = source?.poll.getSnapshot ?? noView;
  return useSyncExternalStore(source?.poll.subscribe ?? subscribeNothing, snapshot, snapshot);
}

/**
 * The time of day of a page time on the player's own clock, on the 24-hour clock the design writes ("14:00"), in
 * the player's locale's digits and separator.
 *
 * @param ms Epoch milliseconds on the page's clock.
 * @returns Such as "14:00".
 */
export function timeOfDay(ms: number): string {
  return new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(ms));
}

/**
 * What the strip says now: the notice for the poll's view, or null when there is no poll or nothing to say. The entry
 * screens read it too, since the wordmark is the small one while a notice shows (ADR-061's amendment "character
 * select as redesigned").
 *
 * @returns The notice, or null.
 */
export function useServerNotice(): ReturnType<typeof serverNotice> {
  const source = useContext(ServerStatusContext);
  const view = useServerView();
  return source === null ? null : serverNotice(view, Date.now(), timeOfDay);
}

/**
 * The strip over an entry screen's window, or nothing when there is nothing to say. The offline strip ends in
 * Check now, which probes at once; the maintenance strip in Details.
 *
 * @param props The window's `width` in logical pixels.
 * @returns The strip, or null.
 */
export function ServerStrip({ width }: { readonly width: number }): ReactElement | null {
  const source = useContext(ServerStatusContext);
  const notice = useServerNotice();
  if (source === null || notice === null) {
    return null;
  }
  const onDetails = source.onDetails;
  const action = notice.check ? (
    <span data-server-action="check">
      <TextLink onClick={() => source.poll.checkNow()}>{say("status.checkNow")}</TextLink>
    </span>
  ) : notice.details && onDetails !== null ? (
    <TextLink onClick={onDetails}>{say("status.details")}</TextLink>
  ) : undefined;
  return <ServerNotice tone={notice.tone} title={notice.title} text={notice.text} action={action} width={width} />;
}
